import { Card, CardContent, CardHeader, createStyles, Grid, Link, Theme, withStyles, WithStyles } from '@material-ui/core';
import React, { Fragment } from 'react';
import { Order } from '../../model/Order';
import OrderRowMetadata from './OrderRowMetadata';
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress';
import Modal from '@material-ui/core/Modal/Modal';
import OrderRowEntry from './OrderRowEntry';
import { OrderItem } from '../../model/OrderItem';
import { EntryAddonProduct } from '../../model/Entry';
import EditEntryDialog from '../Entries/Entry/EditEntryDialog';
import { vasaloppetDateTimeGetterFormatter } from '../../utilities/date';
import { OrderCode } from '../../model/OrderCode';

interface Props {
    order: Order;
    onNavigateToOrder: (orderId: string) => void;
    onForceSave: () => Promise<void>;
    onChange: () => void;
}

interface State {
    saving: boolean;
    editEntryId: string;
}

class ManageOrderItems extends React.Component<Props & WithStyles, State> {
    constructor(props: Props & WithStyles) {
        super(props);
        this.state = { saving: false, editEntryId: null };
    }

    private navigateToOrder(publicId: string) {
        this.props.onNavigateToOrder(publicId)
    }

    private formatRevertReason(reason?: string) {

        if (!reason) {
            return null;
        }

        if (reason.indexOf("Ombokad till ") < 0) {
            return <span>{reason}</span>;
        }

        const orderId = reason.split(":")[0].replace("Ombokad till ", "");
        return <><span>Ombokad till </span><a href="#" style={{ textDecoration: 'underline' }} onClick={() => { this.navigateToOrder(orderId); }}>{orderId}</a><span>:{reason.split(":")[1]}</span></>
    }

    private formatOrderDiscountCodeText(orderCode: OrderCode) {
        if (orderCode.code !== "OMBOKNINGSVÄRDE" || orderCode.description.indexOf("Ombokningvärde från order") < 0) {
            return `${orderCode.code} (${orderCode.description})`;
        }

        const orderId = orderCode.description.split(":")[1].replaceAll(" ", "");
        return <><span>{orderCode.code} </span><span> Ombokningvärde från order: </span><a href="#" style={{ textDecoration: 'underline' }} onClick={() => { this.navigateToOrder(orderId); }}>{orderId}</a><span>)</span></>;
    }

    render() {
        const { classes } = this.props;
        const o = this.props.order;

        const itemPrice = (orderItem: OrderItem): JSX.Element => {
            if (orderItem.originalItemPrice === null || orderItem.originalItemPrice === undefined || orderItem.originalItemPrice === orderItem.itemPrice) {
                return <span>{orderItem.itemPrice} kr</span>;
            }

            return <><span style={{ textDecoration: "line-through" }}>{orderItem.originalItemPrice} kr</span> <span>{orderItem.itemPrice} kr</span></>;
        };

        return <>
            <Card className={classes.card}>
            <CardHeader className={classes.cardHeader} title="Beställning" />
            <CardContent>
                {this.state.saving ? <Modal
                    open={true}
                    aria-labelledby="simple-modal-title"
                    aria-describedby="simple-modal-description"
                >
                    <CircularProgress color="primary" />
                </Modal> : null}
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={2} className={classes.tableHeader}>Antal</Grid>
                    <Grid item xs={6} className={classes.tableHeader}>Produkt</Grid>
                    <Grid item xs={2} className={`${classes.tableHeader} ${classes.rightAlign}`}>à pris</Grid>
                    <Grid item xs={2} className={`${classes.tableHeader} ${classes.rightAlign}`}>Delsumma</Grid>
                    <Grid item xs={12}><div className={classes.headerDivider} /></Grid>
                    {o.items.map((orderItem, idx) => {
                        const variantInfo = orderItem.occationName ? "" : ` (${orderItem.variantName} / ${orderItem.priceGroupName})`;
                        const bg = {};
                        const s = orderItem.reverted ? { textDecoration: "line-through", color: "#9e9e9e" } : {};
                        return <Fragment key={idx}>
                            <Grid style={s} item xs={2}>{orderItem.numberOfProducts} st</Grid>
                            <Grid style={s} item xs={6}>
                                <span className={classes.productName}>{orderItem.productName}</span>
                                {variantInfo && <span className={classes.productMeta}>{variantInfo}</span>}
                            </Grid>
                            <Grid style={s} item xs={2} className={classes.rightAlign}>{itemPrice(orderItem)}</Grid>
                            <Grid style={s} item xs={2} className={classes.rightAlign}>{orderItem.itemPrice * orderItem.numberOfProducts} kr</Grid>
                            {(orderItem.metadata || orderItem.occationName || orderItem.product.type == "insurance" || orderItem.product.name.startsWith("Enervit")) &&
                                <OrderRowMetadata rowStyle={{ ...s, ...bg }} item={orderItem}
                                    onUpdateMetadata={async (metadata) => {
                                        orderItem.metadata = metadata;
                                        return this.props.onForceSave();
                                    }
                                    } />
                            }
                            {(orderItem.entry) &&
                                <OrderRowEntry
                                    rowStyle={{ ...s, ...bg }}
                                    item={orderItem}
                                    onConvertEntryAddonToOwnItem={(toRemoveEntryAddon: EntryAddonProduct, newOrderItem: OrderItem) => {
                                        o.items.push(newOrderItem);
                                        orderItem.entry.addOns = orderItem.entry.addOns.filter(x => x != toRemoveEntryAddon);
                                        this.props.onForceSave();
                                    }}
                                    onChange={() => {
                                        this.props.onChange();
                                    }}
                                />
                            }
                            {orderItem.metadata?.some(x => x.key === "entryId") && (<>
                                <Grid item style={bg} xs={2}></Grid>
                                <Grid item style={bg} xs={10}>
                                    <Link href="#" color="secondary" style={{ fontSize: 13, fontWeight: 500 }}
                                        onClick={(e: React.MouseEvent) => { e.preventDefault(); this.setState({ editEntryId: orderItem.metadata.find(x => x.key === "entryId").value }); }}>
                                        Öppna kopplad anmälan
                                    </Link>
                                </Grid>
                            </>)}
                            {orderItem.code && (<>
                                <Grid item style={bg} xs={2}></Grid>
                                <Grid item style={bg} xs={6} className={classes.codeText}>
                                    Kod: {orderItem.code}
                                </Grid>
                                <Grid item style={bg} xs={4}></Grid>
                            </>)}
                            {orderItem.reverted && <>
                                <Grid item style={bg} xs={2} ></Grid>
                                <Grid item style={bg} xs={10}>
                                    <div className={classes.revertBox}>
                                        <b>{vasaloppetDateTimeGetterFormatter(orderItem.revertedWhen)} ({orderItem.revertedBy}):</b> {this.formatRevertReason(orderItem.revertedReason)}
                                    </div>
                                </Grid></>
                            }
                            <Grid item xs={12}><div className={classes.rowDivider} /></Grid>
                        </Fragment>
                    })}
                    {o.discountCodes && o.discountCodes.map((code, idx) => {
                        return <Fragment key={idx}>
                            <Grid item xs={2}>1</Grid>
                            <Grid item xs={6} className={classes.discountText}>{this.formatOrderDiscountCodeText(code)}</Grid>
                            <Grid item xs={2} className={`${classes.rightAlign} ${classes.discountText}`}>-{code.value} kr</Grid>
                            <Grid item xs={2} className={`${classes.rightAlign} ${classes.discountText}`}>-{code.value} kr</Grid>
                        </Fragment>
                    })}
                </Grid>

                <div className={classes.totalBar}>
                    <span className={classes.totalLabel}>Totalt</span>
                    <span className={classes.totalAmount}>{o.totalSum} kr</span>
                </div>
            </CardContent>
            </Card>
            {this.state.editEntryId &&
                <EditEntryDialog
                    entryId={this.state.editEntryId}
                    onAbortEdit={() => this.setState({ editEntryId: null })}
                    onSave={() => {
                        this.setState({ editEntryId: null });
                        this.props.onChange();
                    }}
                />
            }
        </>
    }
}

const useStyles = ({ palette, spacing }: Theme) => createStyles({
    card: {
        borderRadius: 10,
        overflow: 'hidden',
    },
    cardHeader: {
        background: palette.secondary.main,
        color: palette.secondary.contrastText,
        padding: '10px 16px',
    },
    tableHeader: {
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        color: palette.text.secondary,
        paddingBottom: 4,
    },
    headerDivider: {
        height: 2,
        background: palette.divider,
        margin: '2px 0 6px 0',
    },
    rowDivider: {
        borderBottom: `1px solid ${palette.divider}`,
        margin: '2px 0',
    },
    productName: {
        fontWeight: 600,
    },
    productMeta: {
        color: palette.text.secondary,
        fontSize: 13,
    },
    rightAlign: {
        textAlign: 'right',
    },
    discountText: {
        color: palette.error.main,
    },
    revertBox: {
        display: 'inline-block',
        color: palette.error.dark,
        background: 'rgba(244, 67, 54, 0.08)',
        borderRadius: 6,
        padding: '4px 10px',
        fontSize: 13,
    },
    codeText: {
        color: palette.text.secondary,
        fontSize: 13,
    },
    totalBar: {
        marginTop: 12,
        padding: '14px 16px',
        background: palette.grey[100],
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    totalLabel: {
        fontSize: 13,
        color: palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        fontWeight: 600,
    },
    totalAmount: {
        fontSize: 22,
        fontWeight: 700,
    },
    form: {
        '& > *': {
            margin: spacing(1),
            width: '25ch',
        },
        '& label.Mui-focused': {
            color: palette.secondary.main,
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: palette.secondary.main,
        },
    }
});

export default withStyles(useStyles)(ManageOrderItems);
